<template>
  <div class="content">
    <b-card class="configurations-card">
      <header slot="header">
        <b-row>
          <b-col :title="labels.basicInformations" md="12">
            <span class="bold">{{ labels.basicInformations }}</span>
          </b-col>
        </b-row>
      </header>
      <b-row>
        <b-col class="pb-3" cols="12" xl="6">
          <b-form-group v-if="!editable">
            <b-form-select
              class="readonly-input"
              :value="period"
              size="lg"
              required
              disabled
            >
              <option value="weekly">
                {{ labels.period.weekly }}
              </option>
              <option value="biweekly">
                {{ labels.period.biweekly }}
              </option>
              <option value="monthly">
                {{ labels.period.monthly }}
              </option>
              <option value="single-shot">
                {{ labels.period.singleShot }}
              </option>
            </b-form-select>
            <label class="custom-input-label">
              {{ labels.period.label }}
            </label>
          </b-form-group>
          <b-form-group
            v-else
            label-for="select-period"
            :label="labels.period.label"
            :description="labels.period.description"
          >
            <b-form-select
              id="select-period"
              class="custom-survey-form-input"
              :value="period"
              size="lg"
              @input="changeProperty($event, 'period')"
            >
              <option value="">
                {{ labels.period.select }}
              </option>
              <option value="weekly">
                {{ labels.period.weekly }}
              </option>
              <option value="biweekly">
                {{ labels.period.biweekly }}
              </option>
              <option value="monthly">
                {{ labels.period.monthly }}
              </option>
              <option value="single-shot">
                {{ labels.period.singleShot }}
              </option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col class="pb-3" cols="12" xl="6">
          <b-form-group
            :label="labels.languages.label"
            :description="labels.languages.description"
          >
            <v-select
              :disabled="isTemplate"
              multiple
              :searchable="false"
              :value="availableLanguages"
              :options="languageOptions"
              :closeOnSelect="true"
              :class="{ 'hide-search': languageOptions.length === 0 }"
              @input="changeProperty($event, 'availableLanguages')"
            >
              <template slot="option" slot-scope="option">
                <div class="language-items-select">
                  <img
                    class="language-icon mr-2"
                    :src="`/icons/languages/${option.label}.svg`"
                  />
                  {{ getLanguage(option.label).idiom }}
                </div>
              </template>
              <template slot="no-options">
                {{ labels.languages.notFound }}
              </template>
              <template slot="selected-option" slot-scope="option">
                <div class="tag-wrap" :title="getLanguage(option.label).idiom">
                  <img
                    class="tag-image"
                    :src="`/icons/languages/${option.label}.svg`"
                  />
                  <span class="tag-label">
                    {{ getLanguage(option.label).idiom }}
                  </span>
                </div>
              </template>
            </v-select>
            <label
              class="custom-input-label"
              v-if="availableLanguages.length === 0"
            >
              {{ labels.languages.select }}
            </label>
          </b-form-group>
        </b-col>
        <b-col class="pb-3" cols="12" xl="6">
          <b-form-group v-if="!editable">
            <b-form-select
              class="readonly-input"
              :value="questionsDistribution"
              size="lg"
              required
              disabled
            >
              <option value="random">
                {{ labels.questionsDistribution.random }}
              </option>
              <option value="ordered">
                {{ labels.questionsDistribution.ordered }}
              </option>
            </b-form-select>
            <label class="custom-input-label">
              {{ labels.questionsDistribution.label }}
            </label>
          </b-form-group>
          <b-form-group
            v-else
            label-for="select-distribution"
            :label="labels.questionsDistribution.label"
            :description="labels.questionsDistribution.description"
          >
            <b-form-select
              id="select-distribution"
              class="custom-survey-form-input"
              :value="questionsDistribution"
              size="lg"
              required
              @input="changeProperty($event, 'questionsDistribution')"
            >
              <option value="">
                {{ labels.questionsDistribution.select }}
              </option>
              <option value="random">
                {{ labels.questionsDistribution.random }}
              </option>
              <option value="ordered">
                {{ labels.questionsDistribution.ordered }}
              </option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col class="pb-2" cols="12" xl="6">
          <b-form-group
            :label="labels.questionsQuantity.label"
            :description="labels.questionsQuantity.description"
          >
            <b-form-input
              v-if="period === 'single-shot'"
              class="readonly-input custom-survey-form-input"
              :value="labels.questionsQuantity.all"
              readonly
            />
            <b-form-input
              v-else
              type="number"
              class="custom-survey-form-input"
              :value="questionsQuantity"
              size="lg"
              required
              @input="changeProperty(Number($event), 'questionsQuantity')"
            >
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <b-card class="configurations-card">
      <header slot="header">
        <b-row>
          <b-col :title="labels.public.title" md="12">
            <div class="public-header">
              <span class="bold">{{ labels.public.title }}</span>
              <b-button
                variant="link"
                class="float-right text-secondary"
                @click="$emit('editSurveyPublic')"
              >
                <i class="material-icons">edit</i>
                {{ labels.public.edit }}
              </b-button>
            </div>
          </b-col>
        </b-row>
      </header>
      <b-row v-if="!hasSurveyPublic">
        <b-col class="no-content">
          <span>{{ labels.public.notFound }}</span>
        </b-col>
      </b-row>
      <div class="public-content" v-if="sendToCompany">
        <b-row>
          <b-col cols="12" xl="11">
            <span class="tag-image mdi mdi-factory"></span>
            {{ labels.public.company }}
          </b-col>
          <b-col cols="12" xl="1">
            <i
              class="material-icons clickable"
              @click="
                $emit('changeSurveyPublicProperty', {
                  property: 'sendToCompany',
                  value: false,
                })
              "
              :title="labels.public.delete"
            >
              delete_forever
            </i>
          </b-col>
        </b-row>
      </div>
      <div class="public-content" v-else>
        <b-row
          class="pb-2"
          v-for="group in groupsWithHierarchy"
          :key="group.id"
        >
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account-multiple"></span>
            <b-link
              target="_blank"
              :to="{
                name: 'dashboard.groups',
                params: { groupId: group._id },
              }"
            >
              {{ group.name }}
            </b-link>
          </b-col>
          <b-col cols="12" xl="3" class="public-content-type">
            <b-badge variant="success">
              {{ labels.public.groupsWithHierarchy }}
            </b-badge>
          </b-col>
          <b-col cols="12" xl="1">
            <i
              class="material-icons clickable"
              @click="
                $emit('removeElementFromListOfObject', {
                  object: 'surveyPublic',
                  property: 'groupsWithHierarchy',
                  value: group._id ? group._id : group.id,
                })
              "
              :title="labels.public.delete"
            >
              delete_forever
            </i>
          </b-col>
        </b-row>
        <b-row class="pb-2" v-for="group in groupsOnly" :key="group.id">
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account-multiple"></span>
            <b-link
              target="_blank"
              :to="{
                name: 'dashboard.groups',
                params: { groupId: group._id },
              }"
            >
              {{ group.name }}
            </b-link>
          </b-col>
          <b-col cols="12" xl="3" class="public-content-type">
            <b-badge variant="warning">{{ labels.public.groupsOnly }}</b-badge>
          </b-col>
          <b-col cols="12" xl="1">
            <i
              class="material-icons clickable"
              @click="
                $emit('removeElementFromListOfObject', {
                  object: 'surveyPublic',
                  property: 'groupsOnly',
                  value: group._id ? group._id : group.id,
                })
              "
              :title="labels.public.delete"
            >
              delete_forever
            </i>
          </b-col>
        </b-row>
        <b-row class="pb-2" v-for="user in users" :key="user.id">
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account"></span>
            {{ `${user.profile.firstname} ${user.profile.lastname}` }}
          </b-col>
          <b-col cols="12" xl="3" class="public-content-type">
            <b-badge variant="info">{{ labels.public.user }}</b-badge>
          </b-col>
          <b-col cols="12" xl="1">
            <i
              class="material-icons clickable"
              @click="
                $emit('removeElementFromListOfObject', {
                  object: 'surveyPublic',
                  property: 'users',
                  value: user._id ? user._id : user.id,
                })
              "
              :title="labels.public.delete"
            >
              delete_forever
            </i>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <b-card class="configurations-card">
      <header slot="header">
        <b-row>
          <b-col :title="labels.access.title" md="12">
            <span class="bold">{{ labels.access.title }}</span>
            <b-button
              variant="link"
              class="float-right text-secondary"
              @click="$emit('editSurveyAccessPermission')"
            >
              <i class="material-icons">edit</i>
              {{ labels.access.edit }}
            </b-button>
          </b-col>
        </b-row>
      </header>
      <div class="public-content">
        <b-row>
          <b-col class="pb-2" cols="12" xl="12">
            <span class="tag-image mdi mdi-account-multiple"></span>
            {{ labels.access.admin }}
          </b-col>
        </b-row>
        <b-row v-if="managersPermission">
          <b-col class="pb-2" cols="12" xl="11">
            <span class="tag-image mdi mdi-account-multiple"></span>
            {{ labels.access.manager }}
          </b-col>
          <b-col cols="12" xl="1">
            <i
              class="material-icons clickable"
              @click="
                $emit('changeSurveyAccessProperty', {
                  property: 'managersPermission',
                  value: false,
                })
              "
              :title="labels.access.delete"
            >
              delete_forever
            </i>
          </b-col>
        </b-row>
        <b-row class="pb-2">
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account"></span>
            {{ creator }}
          </b-col>
          <b-col cols="12" xl="3" class="public-content-type">
            <b-badge variant="primary">{{ labels.access.owner }}</b-badge>
          </b-col>
        </b-row>
        <b-row class="pb-2" v-for="user in surveyAccessUsers" :key="user.id">
          <b-col cols="12" xl="8">
            <span class="tag-image mdi mdi-account"></span>
            {{ `${user.profile.firstname} ${user.profile.lastname}` }}
          </b-col>
          <b-col cols="12" xl="3" class="public-content-type">
            <b-badge variant="info">{{ labels.access.user }}</b-badge>
          </b-col>
          <b-col cols="12" xl="1">
            <i
              class="material-icons clickable"
              @click="
                $emit('removeElementFromListOfObject', {
                  object: 'surveyAccess',
                  property: 'users',
                  value: user._id ? user._id : user.id,
                })
              "
              :title="labels.access.delete"
            >
              delete_forever
            </i>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

const props = {
  period: {
    type: String,
  },
  availableLanguages: {
    type: Array,
  },
  languages: {
    type: Array,
  },
  questionsDistribution: {
    type: String,
  },
  questionsQuantity: {
    type: Number,
  },
  enableFeedbacks: {
    type: Boolean,
  },
  mandatoryQuestions: {
    type: Boolean,
  },
  sendToCompany: {
    type: Boolean,
  },
  groupsWithHierarchy: {
    type: Array,
  },
  groupsOnly: {
    type: Array,
  },
  users: {
    type: Array,
  },
  managersPermission: {
    type: Boolean,
  },
  surveyAccessUsers: {
    type: Array,
  },
  creator: {
    type: String,
  },
  editable: {
    type: Boolean,
    default: true,
  },
}

export default {
  props,
  components: {
    vSelect: () => import('vue-select'),
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      isTemplate: 'customSurveys/isTemplate',
    }),
    labels() {
      return this.resources.component.customSurveys.create.tabs
    },
    languageOptions() {
      return this.languages
        .map(l => l.code)
        .filter(l => !this.availableLanguages.includes(l))
    },
    hasSurveyPublic() {
      return this.sendToCompany
        ? true
        : this.groupsWithHierarchy.length +
            this.groupsOnly.length +
            this.users.length >
          0
        ? true
        : false
    },
  },
  methods: {
    changeProperty(value, property) {
      this.$emit('changeProperty', { property, value })
    },
    getLanguage(code) {
      return this.languages.find(l => l.code === code)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

.configurations-card {
  margin-bottom: 30px;

  .custom-survey-form-input {
    cursor: pointer;
    font-size: 0.9rem !important;
    height: 45px !important;
  }

  .public-content {
    .public-content-type {
      text-align: center;
    }
  }

  .no-content {
    text-align: center;
    padding-top: 50px;
    padding-bottom: 40px;
  }

  .hide-search {
    /deep/ .vs__search {
      display: none;
    }
  }

  .readonly-input {
    background-color: map-get($theme-colors, 'white') !important;
    border-color: map-get($theme-colors, 'white') !important;
    color: #6c757d !important;
    height: 42px !important;
  }

  .language-items-select {
    height: 40px;
    display: flex;
    align-items: center;

    .language-icon {
      width: 26px;
      height: 26px;
    }
  }
}
</style>
